export const CHECKOUT_URL = "https://lipoplanning-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/iNLjejFL5PPSMVAug2tUdY/ECUD-EN-2.png",
  author: "https://cdn.magicpatterns.com/uploads/dorZV4XMj3sgqCcdGNyto6/MICHAEL_CARTER_-_IMPLANTES.png",
};

export const caseQuestions = [
  "What should I look at first?",
  "What matters most?",
  "What questions should I ask?",
  "What should I document?",
  "What deserves further review before moving forward?",
  "How do I make sure I haven't overlooked an important consideration?",
];

export const learningSources = [
  "You can read papers.",
  "You can watch surgical demonstrations.",
  "You can attend conferences.",
  "You can study anatomy.",
  "You can consume hours of educational content.",
];

export const beforeSystem = [
  "You open a new case and aren't sure where to begin.",
  "You jump between notes, articles, lectures and previous cases.",
  "Important observations compete for your attention.",
  "Your case notes aren't always organized in the same way.",
  "You spend too much time preparing before reviewing a case.",
  "Every case feels like a completely different problem.",
  "You finish a case without systematically documenting what you noticed or would review again.",
];

export const withSystem = [
  "You start with a defined planning sequence.",
  "You organize every case using the same framework.",
  "You identify the variables that deserve further review.",
  "You document your observations and priorities.",
  "You separate observations from questions that still need investigation.",
  "You compare educational cases using the same structure.",
  "You finish each case with documented learning points for future review.",
];

export type PainIcon = "anxious" | "clock" | "puzzle" | "clipboard" | "search" | "tired";

export const painPoints: { icon: PainIcon; text: string }[] = [
  { icon: "anxious", text: "You open a new liposuction case and aren't completely sure what you should analyze first." },
  { icon: "clock", text: "You spend too much time going back through notes, lectures and references before reviewing a case." },
  { icon: "puzzle", text: "Aesthetic, anatomical and patient-specific variables compete for your attention at the same time." },
  { icon: "clipboard", text: "Your case-planning notes change from patient to patient instead of following one consistent structure." },
  { icon: "search", text: "You want to get better at recognizing what deserves further investigation before making clinical decisions." },
  { icon: "tired", text: "You finish a case with no structured way to document what you noticed, missed or learned." },
];

export const volumes = [
  {
    numeral: "I",
    title: "The 15-Minute Case Planning Framework™",
    hook: "Stop staring at a case wondering where to begin.",
    intro: "A structured educational framework that organizes your case review into clear stages:",
    items: ["Observe", "Organize", "Identify", "Prioritize", "Question", "Review"],
    style: "flow",
    outro: "Includes the 15-Minute Case Planning Worksheet to practice the framework repeatedly. It doesn't replace your clinical judgment — it gives it a consistent structure.",
  },
  {
    numeral: "II",
    title: "The Liposuction Case Assessment Map™",
    hook: "Organize the complete case before getting lost in details.",
    intro: "Structure your review around:",
    items: ["Patient profile", "Patient objectives", "Relevant history to review", "Treatment-area mapping", "Anatomical observations", "Proportional considerations", "Visual documentation", "Areas requiring additional review", "Questions requiring clarification", "Case-specific considerations"],
    style: "list",
    outro: "Instead of disconnected pieces of information, you see the case as a complete picture.",
  },
  {
    numeral: "III",
    title: "The Lipo Planning Workbook™",
    hook: "The heart of the system.",
    intro: "A repeatable workbook with structured pages for:",
    items: ["Case summary", "Patient objectives", "Relevant observations", "Areas of attention", "Questions to investigate", "Planning priorities", "Documentation prompts", "Risk-review prompts", "Preoperative discussion notes", "Post-case reflection", "Lessons learned", "Future questions"],
    style: "list",
    outro: "Print it. Use it. Complete it. Review it. Then use the same structure on the next case.",
  },
  {
    numeral: "IV",
    title: "The Lipo Case Planning Atlas™",
    hook: "Stop learning only by reading theory.",
    intro: "Work through educational cases with the same framework:",
    items: ["Observe — What do you notice?", "Organize — How would you structure the information?", "Prioritize — What deserves attention first?", "Question — What would you investigate further?", "Review — What information would improve your understanding?", "Reflect — What did you learn from the case?"],
    style: "steps",
    outro: "Transform passive consumption into active, case-based learning.",
  },
  {
    numeral: "V",
    title: "The Difficult Case Review Atlas™",
    hook: "Some cases deserve more careful review.",
    intro: "Structured scenarios where you work through:",
    items: ["What information stands out?", "What deserves further review?", "What questions should be clarified?", "Which variables appear particularly important?", "What information may be missing?", "What should be documented?", "What would you investigate further?", "What can be learned from the case?"],
    style: "list",
    outro: "A learning and review resource — not a surgical protocol.",
  },
  {
    numeral: "VI",
    title: "The Professional Case Learning System™",
    hook: "Turn every case you study into an opportunity to improve.",
    intro: "Templates to build your own educational case archive:",
    items: ["Case documentation", "Case reflection", "Case comparison", "Learning points", "Questions for future review", "Knowledge-gap tracking", "Monthly case review", "Case progress tracking", "Quarterly reflection", "Annual professional review"],
    style: "list",
    outro: "You stop simply finishing cases — and start building your own professional learning library.",
  },
] as const;

export type ToolIcon = "sheet" | "checklist" | "map" | "matrix" | "camera";

export const tools: { icon: ToolIcon; title: string; text: string }[] = [
  { icon: "sheet", title: "The 15-Minute Case Planning Sheet", text: "A structured worksheet for organizing an educational case review from beginning to end." },
  { icon: "checklist", title: "The Pre-Case Review Checklist", text: "Organize the information you want to review before analyzing a case." },
  { icon: "map", title: "The Liposuction Case Assessment Map", text: "A visual framework for organizing observations across the complete case." },
  { icon: "matrix", title: "The Case Comparison Matrix", text: "Compare cases side by side and identify differences, similarities and learning points." },
  { icon: "camera", title: "The Visual Case Documentation Guide", text: "A framework for organizing visual case documentation for educational review." },
];

export const bonuses = [
  {
    number: 1,
    title: "30 Educational Liposuction Cases",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/rEqQvvuBepPNyerByJtirW/BONUS_1_LIPOPLANNING-EN.png",
    text: "A library of case exercises to repeatedly practice the planning framework. Each case follows the same sequence: Case → Observe → Organize → Prioritize → Review → Learn.",
    kicker: "Instead of reading another chapter, you get another opportunity to practice.",
  },
  {
    number: 2,
    title: "The 10-Minute Case Review™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/mmSgX5ySAyZkkHXjoyPUpf/BONUS_2_LIPOPLANNING-EN.png",
    text: "Don't have time for the complete workbook? A compact version of the framework to review a case quickly — perfect for short study sessions, case conferences or daily practice.",
    kicker: "10 minutes. One case. One structured review.",
  },
  {
    number: 3,
    title: "The Lipo Planning Error Atlas™",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/2sFzR5wfnM6pb1QQQt5FFw/BONUS_3_LIPOPLANNING-EN.png",
    text: "Scenarios focused on common case-review and planning oversights: what was overlooked, what deserved review, what question should have been asked and what lesson can be extracted.",
    kicker: "Don't memorize mistakes. Train yourself to look for what deserves attention.",
  },
  {
    number: 4,
    title: "The Professional Case Review Cards™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/5SjQvK2Yjz5kjqk5DntrW1/BONUS_4_LIPOPLANNING-EN.png",
    text: "Printable case-review prompts for individual study, peer discussion, case conferences, teaching sessions and daily practice.",
    kicker: "One card. One question. One case. One learning point.",
  },
  {
    number: 5,
    title: "The 30-Day Lipo Case Planning Challenge™",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/8mxh1LiT4tNKnjUu17nX2h/BONUS_5_LIPOPLANNING-EN.png",
    text: "A 30-day practice calendar that turns structured case review into a consistent learning habit — one case, one exercise, one reflection, one lesson every day.",
    kicker: "In 30 days you'll have a structured record of your case-review practice.",
  },
];

export const testimonials = [
  {
    name: "Dr. Isabella Vargas",
    role: "Plastic Surgeon",
    location: "Miami, USA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/0239134e-b469-4cd1-a56c-a28881bb1c84.jpg",
    quote: "I finally review every case in the same order. My consult notes look the same from patient to patient, and I spend far less time rereading references before I sit down with a case.",
  },
  {
    name: "Dr. Richard Hale",
    role: "Aesthetic Physician",
    location: "Toronto, Canada",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/1a5f74a8-21e3-4e44-9151-92123d4a50e9.jpg",
    quote: "After twenty years in practice I didn't expect a workbook to change my routine. The Question and Review stages made me write down what I'd previously only kept in my head.",
  },
  {
    name: "Dr. Camila Ortiz",
    role: "Plastic Surgery Resident",
    location: "Houston, USA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/9102ada0-2c17-4d0c-87c3-2d8acd7fdd6d.jpg",
    quote: "We use the Case Review Cards in our weekly conference. Residents arrive with organized observations instead of scattered notes — the discussions are sharper and faster.",
  },
  {
    name: "Dr. Daniel Brooks",
    role: "Body Contouring Surgeon",
    location: "London, UK",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/41fd48f4-a582-4b9d-8e6e-8176f386efc5.jpg",
    quote: "The Case Comparison Matrix alone was worth it. Seeing my own cases side by side with the same structure showed me patterns I had never noticed.",
  },
];

export const guaranteeItems = [
  "Open all six volumes",
  "Review the planning framework",
  "Work through the educational cases",
  "Download the worksheets",
  "Explore the bonus materials",
  "Print the planning tools",
];

export const afterBefore = [
  "You consume information passively.",
  "Every case feels like a completely new problem.",
  "Your planning notes are inconsistent.",
  "You don't always know where to begin.",
  "You jump between different educational resources.",
  "You finish reviewing a case without documenting the lesson.",
];

export const afterAfter = [
  "You have a repeatable case-review framework.",
  "You structure an educational case review in about 15 minutes.",
  "You have standardized worksheets for documenting your thinking.",
  "You compare cases using the same framework.",
  "You identify questions that deserve further investigation.",
  "You document what you learned from each case.",
];

export const authorQuestions = [
  "Where do I start?",
  "What should I look at?",
  "What should I prioritize?",
  "What questions should I ask?",
  "What should I document?",
  "What did I learn from this case?",
];

export const faqs = [
  { q: "How will I receive Lipo Planning Workbook™?", a: "Immediately after purchase, you'll receive access to the digital materials. The workbooks, educational cases, checklists and bonus resources are provided in downloadable digital formats." },
  { q: "Who is Lipo Planning Workbook™ for?", a: "Qualified plastic surgeons, aesthetic physicians and other appropriately trained healthcare professionals who want a more structured educational framework for reviewing liposuction and body-contouring cases." },
  { q: "Is this a surgical technique course?", a: "No. Lipo Planning Workbook™ is an educational case-planning and review system. It is not intended to replace formal surgical training, supervised clinical experience, mentorship, institutional protocols, current clinical guidelines or professional judgment." },
  { q: "Does the workbook give me a step-by-step surgical protocol?", a: "No. The system helps you organize your professional case review and preoperative thinking. It does not provide a universal surgical recipe or substitute for appropriate clinical training and individualized decision-making." },
  { q: "How long does each case review take?", a: "The core framework is designed around an approximately 15-minute educational case-review session. A shorter 10-minute version is included as Bonus #2." },
  { q: "Can I print the materials?", a: "Yes. The worksheets and planning tools are designed to be downloaded, printed and reused for educational case review." },
  { q: "Is this a one-time payment?", a: "Yes. You pay $27 once. There are no monthly subscriptions and no recurring fees." },
  { q: "How long will I have access?", a: "Your digital access is intended to be lifetime access, subject to the purchase terms and availability of the product." },
  { q: "What happens if I don't like it?", a: "You have a 7-day guarantee. If you decide the system isn't right for your professional learning needs, contact us within the guarantee period according to the purchase terms." },
];

export const recentBuyers = [
  { name: "Dr. Valentina Rojas", place: "Bogotá, Colombia" },
  { name: "Dr. Alejandro Méndez", place: "Mexico City, Mexico" },
  { name: "Dr. Camila Fernández", place: "Buenos Aires, Argentina" },
  { name: "Dr. Javier Castillo", place: "Lima, Peru" },
  { name: "Dr. Lucía Martínez", place: "Madrid, Spain" },
  { name: "Dr. Mateo Herrera", place: "Santiago, Chile" },
  { name: "Dr. Sofía Navarro", place: "Quito, Ecuador" },
  { name: "Dr. Diego Paredes", place: "Santo Domingo, Dominican Republic" },
  { name: "Dr. Gabriela Torres", place: "San José, Costa Rica" },
  { name: "Dr. Andrés Gutiérrez", place: "Montevideo, Uruguay" },
  { name: "Dr. Mariana López", place: "Panama City, Panama" },
  { name: "Dr. Ricardo Salazar", place: "Caracas, Venezuela" },
  { name: "Dr. Paula Jiménez", place: "Guatemala City, Guatemala" },
  { name: "Dr. Fernando Ruiz", place: "Asunción, Paraguay" },
  { name: "Dr. Daniela Vega", place: "San Juan, Puerto Rico" },
];
